import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Documents.css";
import { capture } from "../../../utils/analytics";
import pdfIcon from "images/pdf_symbol.png";
import xlsxIcon from "images/xlsx_symbol.png";
import docxIcon from "images/docx_symbol.png";
import dwgIcon from "images/dwg_symbol.png";
import stpIcon from "images/stp_symbol.png";
import xmlIcon from "images/xml_symbol.png";
import trashcanIcon from "images/trashcan.svg";
import arrowRightIcon from "images/arrow_right.svg";
import penIcon from "images/pen.svg";
import checkIcon from "images/check.svg";
import crossIcon from "images/cross.svg";
import { useDocumentTypes } from "../../../contexts/DocumentTypesContext";
import { useOffline } from "../../../offline/OfflineProvider";
import { getAPIBaseURL } from "../../../utils/api";
import { createNestedParts } from "../../../utils/createNestedParts";
import { renderOptions } from "../../../utils/renderOptions";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaEdit, FaTrash } from "react-icons/fa";

const API_BASE_URL = getAPIBaseURL();

function Documents({
  documents,
  setDocuments,
  onRemoveDocument,
  refreshDocuments,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  isLoading,
  setIsLoading,
  parts,
  onOpenDocument,
  hidePartFilter,
  hideComponentColumn,
}) {
  const [selectedType, setSelectedType] = useState("");
  const { fileSync } = useOffline();
  const cachedKeys = fileSync?.cachedKeys;
  const [selectedPart, setSelectedPartFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingDocument, setEditingDocument] = useState(null);
  const [editedDisplayName, setEditedDisplayName] = useState("");
  const [editedDocType, setEditedDocType] = useState("");
  const [editedPartId, setEditedPartId] = useState("");
  const [editedPartName, setEditedPartName] = useState("");
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null); // null | 'type' | 'part'
  const [bulkTypeValue, setBulkTypeValue] = useState('');
  const [bulkPartId, setBulkPartId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { doc, displayName }
  const [openMenuId, setOpenMenuId] = useState(null); // row whose action menu is open
  const menuRef = useRef(null);
  const { documentTypes = [] } = useDocumentTypes() || {};

  /* ------------------------- effects & helpers ------------------------- */

  useEffect(() => {
    refreshDocuments();
  }, []);

  const toggleMenu = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  // close the row action menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Utility functions for metadata display
  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Enhanced filtering and searching
  const filtered = useMemo(() => {
    if (!Array.isArray(documents)) return [];

    return documents.filter((doc) => {
      if (!doc) return false;
      const matchesType = selectedType === "" || doc.doctype === selectedType;
      const matchesPart = selectedPart === "" || doc.partId === selectedPart;
      const matchesSearch = searchQuery === "" ||
        (doc.displayName || doc.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.doctype || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.partName || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesPart && matchesSearch;
    });
  }, [documents, selectedType, selectedPart, searchQuery]);

  // Derived sort — keeps the user's chosen order across refetches
  const sorted = useMemo(() => {
    const dir = sortOrder === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortField === "pageCount") {
        return ((a.pageCount ?? -1) - (b.pageCount ?? -1)) * dir;
      }
      const va = String(a[sortField] || "").toUpperCase();
      const vb = String(b[sortField] || "").toUpperCase();
      return va.localeCompare(vb) * dir;
    });
  }, [filtered, sortField, sortOrder]);


  // Statistics
  const stats = useMemo(() => {
    if (!Array.isArray(documents)) {
      return { total: 0, active: 0, inactive: 0, byType: {} };
    }

    const total = documents.length;
    const active = documents.filter(doc => doc && doc.isActive !== false).length;
    const byType = {};
    documents.forEach(doc => {
      if (!doc) return;
      const type = doc.doctype || "Unbekannt";
      byType[type] = (byType[type] || 0) + 1;
    });
    return { total, active, inactive: total - active, byType };
  }, [documents]);

  const getFileIcon = (name) => {
    // Guard clause: return default icon if name is undefined/null
    if (!name) {
      return pdfIcon;
    }
    const ext = name.split(".").pop().toLowerCase();
    return (
      {
        xlsx: xlsxIcon,
        docx: docxIcon,
        dwg: dwgIcon,
        stp: stpIcon,
        xml: xmlIcon,
      }[ext] || pdfIcon
    );
  };

  if (!Array.isArray(documents)) return <div>Error loading documents.</div>;

  /* --------------------------- backend calls --------------------------- */

  const handleRemoveClick = (doc, e) => {
    e.preventDefault();
    setConfirmDelete({ doc, displayName: doc?.displayName || doc?.name });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { bulk, count, doc, displayName } = confirmDelete;
    setConfirmDelete(null);
    setIsLoading(true);
    try {
      if (bulk) {
        for (const docName of selectedDocuments) {
          await onRemoveDocument(docName);
        }
        setSelectedDocuments(new Set());
        setShowBulkActions(false);
        const successMsg = document.createElement('div');
        successMsg.className = 'toast success';
        successMsg.textContent = `${count} Dokumente wurden erfolgreich gelöscht`;
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
      } else {
        await onRemoveDocument(doc.name, doc.docId || doc._id);
        const successMsg = document.createElement('div');
        successMsg.className = 'toast success';
        successMsg.textContent = `Dokument "${displayName}" wurde erfolgreich gelöscht`;
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
      }
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'toast error';
      errorMsg.textContent = bulk ? 'Fehler beim Löschen der Dokumente' : 'Fehler beim Löschen des Dokuments';
      document.body.appendChild(errorMsg);
      setTimeout(() => errorMsg.remove(), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentClick = async (doc, e) => {
    e.preventDefault();

    const fileName = doc.name || doc.displayName || "";
    const ext = fileName.split(".").pop().toLowerCase();

    // Inline-viewable types
    const inlineTypes = { pdf: "pdf", docx: "docx", xlsx: "xlsx", csv: "csv", xml: "xml", stp: "stp", dwg: "dwg" };

    if (inlineTypes[ext] && onOpenDocument) {
      capture("document_opened", { docName: doc.displayName || doc.name, ext });
      onOpenDocument({
        type: inlineTypes[ext],
        url: doc.path,
        displayName: doc.displayName || doc.name,
        ...(ext === "pdf" ? { page: 1, highlightTexts: [] } : {}),
      });
      return;
    }

    // Fallback: download (unknown types)
    try {
      capture("document_downloaded", { docName: doc.displayName || doc.name, ext });
      const { downloadFile } = await import("../../../utils/downloadFile");
      await downloadFile(doc.path, doc.name);


    } catch (err) {
      console.error("Download failed:", err);
    }
  };


  const handleEditClick = (doc) => {
    setEditingDocument(doc);
    setEditedDisplayName(doc.displayName || "");
    setEditedDocType(doc.doctype || "");
    setEditedPartId(doc.partId || "");
    setEditedPartName(doc.partName || "");
  };

  const handleCancelEdit = () => {
    setEditingDocument(null);
    setEditedDisplayName("");
    setEditedDocType("");
  };

  const handleSaveEdit = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/documents/updateDocument`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          docId: editingDocument.docId || editingDocument._id,
          name: editingDocument.name,
          displayName: editedDisplayName,
          doctype: editedDocType,
          partId: editedPartId,
          partName: editedPartName,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Update failed");
      const { updatedFields } = await r.json();
      setDocuments((prev) =>
        prev.map((d) => (d.docId === editingDocument.docId || d._id === editingDocument._id ? { ...d, ...updatedFields } : d))
      );
      setEditingDocument(null);

      // Show success feedback
      const successMsg = document.createElement('div');
      successMsg.className = 'toast success';
      successMsg.textContent = 'Dokument wurde erfolgreich aktualisiert';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
    } catch (err) {
      console.error("Update error:", err);
      // Show error feedback
      const errorMsg = document.createElement('div');
      errorMsg.className = 'toast error';
      errorMsg.textContent = 'Fehler beim Aktualisieren des Dokuments';
      document.body.appendChild(errorMsg);
      setTimeout(() => errorMsg.remove(), 3000);
    }
  };

  /* ------------------------- toggle isActive --------------------------- */

  const handleToggleActive = async (doc) => {
    const docIdentity = doc.docId || doc._id;
    const optimistic = !doc.isActive;
    setDocuments((prev) =>
      prev.map((d) => ((d.docId || d._id) === docIdentity ? { ...d, isActive: optimistic } : d))
    );
    try {
      const r = await fetch(`${API_BASE_URL}/api/documents/toggle-active`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docId: docIdentity, name: doc.name }),
      });
      if (!r.ok) throw new Error("Toggle request failed");
      const { isActive } = await r.json();
      setDocuments((prev) =>
        prev.map((d) => ((d.docId || d._id) === docIdentity ? { ...d, isActive } : d))
      );
    } catch (err) {
      console.error("Toggle failed, reverting:", err);
      setDocuments((prev) =>
        prev.map((d) => ((d.docId || d._id) === docIdentity ? { ...d, isActive: doc.isActive } : d))
      );
    }
  };

  /* ------------------------------- UI ---------------------------------- */

  // Bulk selection handlers
  const handleSelectDocument = (docName) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(docName)) {
      newSelected.delete(docName);
    } else {
      newSelected.add(docName);
    }
    setSelectedDocuments(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedDocuments.size === filtered.length) {
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedDocuments(new Set(filtered.map(doc => doc.name)));
      setShowBulkActions(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedDocuments.size === 0) return;
    setConfirmDelete({ bulk: true, count: selectedDocuments.size });
  };

  const showToast = (type, msg) => {
    const el = document.createElement('div');
    el.className = `toast ${type}`;
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  };

  const handleBulkApplyType = async () => {
    if (!bulkTypeValue) return;
    const selectedDocs = documents.filter(doc => selectedDocuments.has(doc.name));
    setDocuments(prev => prev.map(d => selectedDocuments.has(d.name) ? { ...d, doctype: bulkTypeValue } : d));
    setBulkMode(null);
    try {
      await Promise.all(selectedDocs.map(doc =>
        fetch(`${API_BASE_URL}/api/documents/updateDocument`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docId: doc.docId || doc._id, name: doc.name,
            displayName: doc.displayName, doctype: bulkTypeValue,
            partId: doc.partId, partName: doc.partName,
          }),
        })
      ));
      showToast('success', `${selectedDocs.length} Dokument${selectedDocs.length !== 1 ? 'e' : ''} aktualisiert`);
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
    } catch {
      showToast('error', 'Fehler beim Aktualisieren');
      refreshDocuments();
    }
  };

  const handleBulkApplyPart = async () => {
    if (!bulkPartId) return;
    const part = parts.find(p => String(p.id) === String(bulkPartId));
    const partName = part?.name || '';
    const selectedDocs = documents.filter(doc => selectedDocuments.has(doc.name));
    setDocuments(prev => prev.map(d => selectedDocuments.has(d.name) ? { ...d, partId: bulkPartId, partName } : d));
    setBulkMode(null);
    try {
      await Promise.all(selectedDocs.map(doc =>
        fetch(`${API_BASE_URL}/api/documents/updateDocument`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docId: doc.docId || doc._id, name: doc.name,
            displayName: doc.displayName, doctype: doc.doctype,
            partId: bulkPartId, partName,
          }),
        })
      ));
      showToast('success', `${selectedDocs.length} Dokument${selectedDocs.length !== 1 ? 'e' : ''} aktualisiert`);
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
    } catch {
      showToast('error', 'Fehler beim Aktualisieren');
      refreshDocuments();
    }
  };

  const handleBulkSetActive = async (targetActive) => {
    const selectedDocs = documents.filter(doc => selectedDocuments.has(doc.name));
    const toChange = selectedDocs.filter(doc => doc.isActive !== targetActive);
    if (toChange.length === 0) return;
    setDocuments(prev => prev.map(d => selectedDocuments.has(d.name) ? { ...d, isActive: targetActive } : d));
    try {
      await Promise.all(toChange.map(doc =>
        fetch(`${API_BASE_URL}/api/documents/toggle-active`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docId: doc.docId || doc._id, name: doc.name }),
        })
      ));
      showToast('success', `${toChange.length} Dokument${toChange.length !== 1 ? 'e' : ''} ${targetActive ? 'aktiviert' : 'deaktiviert'}`);
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
    } catch {
      showToast('error', 'Fehler beim Aktualisieren');
      refreshDocuments();
    }
  };

  return (
    <section>
      {/* Single toolbar: search + filter + stats + view toggle */}
      <div className="documents-toolbar">
        <div className="doc-input doc-input--search">
          <input
            type="text"
            placeholder="Dokumente durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="doc-text-input"
          />
          {searchQuery && (
            <button
              type="button"
              className="input-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Suche löschen"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 5l6 6M11 5l-6 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="doc-input doc-input--select">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="doc-select"
          >
            <option value="">Alle Dokumenttypen</option>
            {documentTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {!hidePartFilter && (
          <div className="doc-input doc-input--select">
            <select
              value={selectedPart}
              onChange={(e) => setSelectedPartFilter(e.target.value)}
              className="doc-select"
            >
              <option value="">Alle Komponenten</option>
              {renderOptions(createNestedParts(parts))}
            </select>
          </div>
        )}

        {/* Sort controls — field select + direction toggle */}
        <div className="doc-input doc-input--select doc-input--sort">
          <select
            className="doc-select"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            aria-label="Sortieren nach"
          >
            <option value="displayName">Sortieren: Dateiname</option>
            <option value="doctype">Sortieren: Dokumenttyp</option>
            {!hideComponentColumn && (
              <option value="partName">Sortieren: Komponente</option>
            )}
            <option value="pageCount">Sortieren: Seiten</option>
          </select>
        </div>
        <button
          type="button"
          className="sort-direction-btn"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          title={sortOrder === "asc" ? "Aufsteigend" : "Absteigend"}
          aria-label={
            sortOrder === "asc"
              ? "Aufsteigend sortiert – klicken für absteigend"
              : "Absteigend sortiert – klicken für aufsteigend"
          }
        >
          <img src={arrowRightIcon} alt="" className={`sort-icon ${sortOrder}`} />
        </button>

        {/* Stats + View Controls — right side of toolbar */}
        <div className="toolbar-right">
          {stats.total > 0 && (
            <span className="documents-stats">
              <span className="stat-badge">
                {stats.total} {stats.total === 1 ? 'Dokument' : 'Dokumente'}
              </span>
              {stats.inactive > 0 && (
                <span className="stat-badge inactive">
                  {stats.inactive} inaktiv
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Bulk Actions — separate bar */}
      {showBulkActions && (
        <div className="bulk-actions">
          <span className="selected-count">
            {selectedDocuments.size} {selectedDocuments.size === 1 ? 'Dokument' : 'Dokumente'} ausgewählt
          </span>
          <div className="bulk-action-buttons">
            <button type="button" className="bulk-btn select-all" onClick={handleSelectAll}>
              {selectedDocuments.size === filtered.length ? 'Auswahl aufheben' : 'Alle auswählen'}
            </button>

            {bulkMode === 'type' ? (
              <>
                <select className="bulk-select" value={bulkTypeValue} onChange={e => setBulkTypeValue(e.target.value)} autoFocus>
                  <option value="">Typ wählen…</option>
                  {documentTypes.map(dt => {
                    const v = dt.type || dt;
                    return <option key={v} value={v}>{v}</option>;
                  })}
                </select>
                <button type="button" className="bulk-btn" onClick={handleBulkApplyType} disabled={!bulkTypeValue}>OK</button>
                <button type="button" className="bulk-btn" onClick={() => setBulkMode(null)}>✕</button>
              </>
            ) : (
              <button type="button" className="bulk-btn" onClick={() => { setBulkMode('type'); setBulkTypeValue(''); }}>Typ ändern</button>
            )}

            {bulkMode === 'part' ? (
              <>
                <select className="bulk-select" value={bulkPartId} onChange={e => setBulkPartId(e.target.value)} autoFocus>
                  <option value="">Komponente wählen…</option>
                  {renderOptions(createNestedParts(parts))}
                </select>
                <button type="button" className="bulk-btn" onClick={handleBulkApplyPart} disabled={!bulkPartId}>OK</button>
                <button type="button" className="bulk-btn" onClick={() => setBulkMode(null)}>✕</button>
              </>
            ) : (
              <button type="button" className="bulk-btn" onClick={() => { setBulkMode('part'); setBulkPartId(''); }}>Komponente</button>
            )}

            <button type="button" className="bulk-btn" onClick={() => handleBulkSetActive(true)}>Aktivieren</button>
            <button type="button" className="bulk-btn" onClick={() => handleBulkSetActive(false)}>Deaktivieren</button>

            <button type="button" className="bulk-btn delete" onClick={handleBulkDelete}>
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '14px', height: '14px' }}>
                <path d="M2 4h12M5.5 4V2.5A1.5 1.5 0 017 1h2a1.5 1.5 0 011.5 1.5V4m2 0v9.5A1.5 1.5 0 0111 15H5a1.5 1.5 0 01-1.5-1.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>Löschen</span>
            </button>
          </div>
        </div>
      )}

      {/* List View */}
      <div className={`documents-grid list ${hideComponentColumn ? "hide-component" : ""}`}>
          {/* header */}
          <div className="document-tile header-tile">
            {/* Selection checkbox */}
            <div className="select-column">
              <input
                type="checkbox"
                checked={selectedDocuments.size === filtered.length && filtered.length > 0}
                onChange={handleSelectAll}
                className="select-checkbox"
                title="Alle auswählen"
              />
            </div>

            <div className="document-info">
              <span className="list-preview header-placeholder"></span>
              <span className="document-name">Dateiname</span>
            </div>

            {/* exact‑width toggle placeholder */}
            <span
              className="toggle-placeholder"
              title="Inaktive Dokumente werden von Suche und ALLRECObot ausgeschlossen"
            >
              Aktiv
            </span>

            {/* action placeholder */}
            <span className="doc-actions header-placeholder"></span>
          </div>

          {/* rows */}
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="documents-placeholder">
              Keine Dokumente für den ausgewählten Filter verfügbar.
            </div>
          ) : (
            sorted.map((doc) => {
              const inactive = doc.isActive === false;
              return (
                <div
                  className={`document-tile${inactive ? " inactive" : ""}${selectedDocuments.has(doc.name) ? " selected" : ""}`}
                  key={doc._id || `${doc.name}-${doc.path}`}
                >
                  {/* Selection checkbox */}
                  <div className="select-column">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.has(doc.name)}
                      onChange={() => handleSelectDocument(doc.name)}
                      className="select-checkbox"
                    />
                  </div>

                  {/* preview + name */}
                  <div className="document-info">
                    <div
                      className="list-preview"
                      onClick={(e) => handleDocumentClick(doc, e)}
                    >
                      {doc.name && doc.path && /\.(pdf|docx)$/i.test(doc.name) && (
                        <img
                          src={`${API_BASE_URL}/api/thumbnail/${doc.path.split('/').pop()}`}
                          alt=""
                          className="list-preview-img"
                          loading="lazy"
                          onError={(e) => { e.target.style.display='none'; }}
                        />
                      )}
                      <img
                        src={getFileIcon(doc.name)}
                        alt="File"
                        className="list-preview-fallback"
                      />
                    </div>
                    <div className="document-text">
                      {editingDocument === doc ? (
                        <input
                          type="text"
                          value={editedDisplayName}
                          onChange={(e) => setEditedDisplayName(e.target.value)}
                          className="edit-input"
                          autoFocus
                        />
                      ) : (
                        <a
                          href={doc.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-link"
                          onClick={(e) => handleDocumentClick(doc, e)}
                        >
                          <span className="document-name">
                            {doc.displayName || doc.name}
                          </span>
                        </a>
                      )}

                      {cachedKeys && doc.path && (cachedKeys.has(doc.path) || cachedKeys.has(`${doc.path}/preview.glb`)) && (
                        <span className="offline-mark" title="Offline verfügbar">offline</span>
                      )}

                      {/* metadata tags */}
                      <div className="document-tags">
                        {editingDocument === doc ? (
                          <>
                            <select
                              className="tag-edit-select"
                              value={editedDocType}
                              onChange={(e) => setEditedDocType(e.target.value)}
                            >
                              {documentTypes.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                            {!hideComponentColumn && (
                              <select
                                className="tag-edit-select"
                                value={editedPartId}
                                onChange={(e) => {
                                  const id = e.target.value;
                                  const part = parts.find((p) => p.id === id);
                                  setEditedPartId(id);
                                  setEditedPartName(part ? part.name : "");
                                }}
                              >
                                <option value="" disabled hidden>
                                  Komponente auswählen
                                </option>
                                {renderOptions(createNestedParts(parts))}
                              </select>
                            )}
                            {doc.pageCount > 0 && (
                              <span className="metadata-badge page-badge">
                                {doc.pageCount} S.
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {doc.doctype && (
                              <span
                                className="metadata-badge type-badge"
                                title={doc.doctype}
                              >
                                {doc.doctype}
                              </span>
                            )}
                            {!hideComponentColumn && doc.partName && (
                              <span
                                className="metadata-badge part-badge"
                                title={doc.partName}
                              >
                                {doc.partName}
                              </span>
                            )}
                            {doc.pageCount > 0 && (
                              <span className="metadata-badge page-badge">
                                {doc.pageCount} S.
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* toggle */}
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={doc.isActive !== false}
                      onChange={() => handleToggleActive(doc)}
                    />
                    <span className="toggle-slider"></span>
                  </label>

                  {/* actions */}
                  <div className="doc-actions">
                    {editingDocument === doc ? (
                      <>
                        <button
                          aria-label="Speichern"
                          className="icon-btn edit-icon"
                          onClick={handleSaveEdit}
                        >
                          <img src={checkIcon} alt="" />
                        </button>
                        <button
                          aria-label="Abbrechen"
                          className="icon-btn edit-icon"
                          onClick={handleCancelEdit}
                        >
                          <img src={crossIcon} alt="" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="doc-menu-trigger"
                          onClick={(e) => toggleMenu(e, doc.name)}
                          aria-label="Menü öffnen"
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === doc.name}
                        >
                          <BsThreeDotsVertical />
                        </button>
                        {openMenuId === doc.name && (
                          <ul
                            className="doc-menu"
                            ref={menuRef}
                            onClick={(e) => e.stopPropagation()}
                            role="menu"
                          >
                            <li
                              role="menuitem"
                              onClick={() => {
                                setOpenMenuId(null);
                                handleEditClick(doc);
                              }}
                            >
                              <FaEdit className="menu-item-icon" />
                              Bearbeiten
                            </li>
                            <li
                              className="delete"
                              role="menuitem"
                              onClick={(e) => {
                                setOpenMenuId(null);
                                handleRemoveClick(doc, e);
                              }}
                            >
                              <FaTrash className="menu-item-icon" />
                              Löschen
                            </li>
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p>
              {confirmDelete.bulk
                ? <>Möchten Sie wirklich <strong>{confirmDelete.count} {confirmDelete.count === 1 ? 'Dokument' : 'Dokumente'}</strong> endgültig löschen?</>
                : <>Möchten Sie das Dokument <strong>"{confirmDelete.displayName}"</strong> wirklich endgültig löschen?</>
              }
            </p>
            <p className="confirm-modal-warning">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="confirm-modal-actions">
              <button className="confirm-modal-cancel" onClick={() => setConfirmDelete(null)}>
                Abbrechen
              </button>
              <button className="confirm-modal-delete" onClick={handleConfirmDelete}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Documents;
